import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { canConfirmDelete, formatVideoCount } from './flowListUtils';

interface DeleteFlowDialogProps {
  flow: { id: string; name: string; video_count: number } | null;
  pending?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (id: string) => void;
}

// Exclusão definitiva: exige digitar o nome do fluxo, porque leva junto os vídeos e as métricas.
export function DeleteFlowDialog({ flow, pending = false, onOpenChange, onConfirm }: DeleteFlowDialogProps) {
  useLanguage();
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (flow) setTyped('');
  }, [flow]);

  const allowed = !!flow && canConfirmDelete(typed, flow.name);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (flow && allowed && !pending) onConfirm(flow.id);
  };

  return (
    <AlertDialog open={!!flow} onOpenChange={onOpenChange}>
      {/* !max-w: a regra global max-width:100vw do index.css venceria um max-w comum. */}
      <AlertDialogContent className="!max-w-[460px]">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Excluir fluxo e vídeos?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Isso apaga o fluxo \"")}{flow?.name}", {formatVideoCount(flow?.video_count || 0)} {" "}{translateInterfaceText("e as métricas dele. O código colado nos sites para de funcionar. Não dá para desfazer.")}</AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex flex-col gap-2">
            <Label htmlFor="delete-flow-confirm">
              {translateInterfaceText("Digite")}{" "}<strong className="font-semibold">{flow?.name}</strong> {" "}{translateInterfaceText("para confirmar")}</Label>
            <Input
              id="delete-flow-confirm"
              value={typed}
              autoComplete="off"
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>

          <AlertDialogFooter className="gap-3 sm:space-x-0">
            <AlertDialogCancel type="button">{translateInterfaceText("Cancelar")}</AlertDialogCancel>
            <Button type="submit" variant="destructive" disabled={!allowed || pending}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {translateInterfaceText("Excluir definitivamente")}</Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
